import { Link } from "react-router-dom";
import { Compass, Scale, ShieldCheck, ArrowRight } from "lucide-react";
import { GoldBadge, SectionHead, Bullets, Reveal } from "@/components/shared";

const SERVICES = [
  {
    code: "GSI-01",
    icon: Compass,
    title: "Advisory & Mentoring",
    tagline: "Direct survival coaching and guidance frameworks.",
    bullets: [
      "One-on-one survival coaching for survivors of abuse and institutional neglect",
      "Structured guidance frameworks for rebuilding safety, stability, and self-direction",
      "Long-term mentoring pathways led by people who have lived the system",
      "Warm referrals into legal, housing, mental-health, and crisis services",
    ],
    testId: "service-advisory-module",
  },
  {
    code: "GSI-02",
    icon: Scale,
    title: "Advocacy & Court Support",
    tagline: "Standing face-to-face with institutions and walking alongside survivors through legal systems.",
    bullets: [
      "In-person court accompaniment — no survivor faces the bench alone",
      "Direct institutional advocacy: formal complaints, apologies, and accountability",
      "Preparation support for statements, evidence, and official proceedings",
      "Guidance through Royal Commission, police, and child-protection reporting channels",
    ],
    testId: "service-advocacy-module",
  },
  {
    code: "GSI-03",
    icon: ShieldCheck,
    title: "Community Safety Patrols",
    tagline: "Active mobile observation initiatives to lower escalating child abuse statistics.",
    bullets: [
      "Mobile car and foot patrols maintaining a visible protective presence",
      "Systematic observation and reporting of escalating risk situations",
      "Rapid escalation of incidents to police and relevant authorities",
      "Community reassurance through consistent, accountable patrol coverage",
    ],
    testId: "service-patrols-module",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="border-b border-white/10 bg-[#111622] grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <GoldBadge testId="services-badge">Official Structural Index</GoldBadge>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50">
              The Global <span className="gold-text-gradient">Service Index</span>
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg leading-relaxed text-slate-300">
              An authoritative index of our structural branches of frontline intervention and support.
              Every branch is active, accountable, and open to survivors, families, and community
              members who need it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 space-y-10">
        {SERVICES.map((s, i) => (
          <Reveal key={s.testId} delay={0.05}>
            <article
              data-testid={s.testId}
              className="card-pain p-6 sm:p-10 grid gap-8 lg:grid-cols-[auto_1fr_1.2fr] lg:items-start"
            >
              <div className="flex lg:flex-col items-center lg:items-start gap-4">
                <span className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90">
                  {s.code}
                </span>
                <span className="flex items-center justify-center h-14 w-14 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                  <s.icon className="h-7 w-7 text-amber-400" />
                </span>
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50">
                  {s.title}
                </h2>
                <div className="h-[2px] w-16 bg-gradient-to-r from-amber-400 via-amber-600 to-transparent my-4" />
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{s.tagline}</p>
              </div>
              <Bullets items={s.bullets} />
            </article>
          </Reveal>
        ))}

        <Reveal>
          <div data-testid="services-access-card" className="card-pain p-8 sm:p-10">
            <SectionHead
              eyebrow="Access Protocol"
              title="How to access Global Service Index resources"
            />
            <div className="mt-6 grid gap-8 md:grid-cols-2">
              <Bullets
                items={[
                  "Submit a confidential request through the Survivor Safe Portal on our Contact page",
                  "Select the branch you need — Advisory & Mentoring, Advocacy & Court Support, or Community Safety Patrols",
                  "A P.A.I.N. advocate will respond directly and arrange next steps at your pace",
                  "You may remain anonymous until you choose otherwise",
                ]}
              />
              <Bullets
                items={[
                  "If a child is in immediate danger, call 000 first — then contact us for ongoing support",
                  "Court support requests should include your hearing date and jurisdiction where safe to share",
                  "All enquiries are handled in strict confidence by trained survivor advocates",
                ]}
              />
            </div>
            <Link
              to="/contact"
              data-testid="services-access-portal-button"
              className="btn-royal mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
            >
              Open the Survivor Safe Portal
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
